import { useState } from 'react';
import { Box, Typography, Button, Grid, Alert, Chip, Paper, Tabs, Tab } from '@mui/material';
import { Add, Refresh, Security, Warning, Info } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDataProvider } from 'react-admin';
import { useWorkflowList, WorkflowCard, WorkflowFilters } from '@/components/workflow';
import type { WorkflowInstance } from '@/components/workflow';

export const ComplianceWorkflowManager = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState<{ status?: string[]; workflowType?: string[]; search?: string }>({
    workflowType: ['contract-alert'],
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { workflows, total, isLoading, error, refetch } = useWorkflowList({
    dataProvider,
    resource: 'workflow-instances',
    filters,
    perPage: 50,
  });

  const activeCount = workflows.filter(w => w.status === 'active').length;
  const pausedCount = workflows.filter(w => w.status === 'paused').length;
  const completedCount = workflows.filter(w => w.status === 'completed' || w.status === 'cancelled').length;

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
    refetch();
  };

  const handleWorkflowOpen = (workflow: WorkflowInstance) => {
    navigate(`/wizard/contract-alert/${workflow.entityId}`);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      setFilters({ workflowType: ['contract-alert'] });
    } else {
      setFilters({});
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Compliance Workflows
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage compliance and contract alert workflows
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }} icon={<Info />}>
        Contract alerts are typically short-lived. Most action happens via the Contract Alerts list.
      </Alert>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Contract Alerts" />
        <Tab label="All Compliance" />
      </Tabs>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper sx={{ p: 2, borderTop: '4px solid #1976d2' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
              Active
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
              {activeCount}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper sx={{ p: 2, borderTop: '4px solid #ed6c02' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
              Paused
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#ed6c02' }}>
              {pausedCount}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper sx={{ p: 2, borderTop: '4px solid #2e7d32' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
              Completed
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32' }}>
              {completedCount}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<Security />}
          onClick={() => navigate('/compliance-calendar')}
        >
          Compliance Calendar
        </Button>
        <Button
          variant="outlined"
          startIcon={<Warning />}
          onClick={() => navigate('/contract-alerts')}
        >
          Contract Alerts List
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <WorkflowFilters />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Typography color="text.secondary">Loading workflows...</Typography>
      ) : workflows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Security sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            No Compliance Workflows Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No compliance workflows match your current filters or no workflows have been initiated yet.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<Security />}
              onClick={() => navigate('/compliance-calendar')}
            >
              Compliance Calendar
            </Button>
            <Button
              variant="outlined"
              startIcon={<Warning />}
              onClick={() => navigate('/contract-alerts')}
            >
              Contract Alerts List
            </Button>
          </Box>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {workflows.map(workflow => (
            <Grid key={workflow.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <WorkflowCard
                workflow={workflow}
                onOpen={handleWorkflowOpen}
                showActions={false}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Total: {total} workflow(s)
        </Typography>
        <Chip
          icon={<Security />}
          label="Compliance"
          size="small"
          variant="outlined"
        />
      </Box>
    </Box>
  );
};